import { useState, useEffect, useCallback, useRef } from 'react';
import { TopBar } from '../components/layout/TopBar';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  Plus, Edit2, Trash2, Loader2, ChevronLeft, ChevronRight,
  Star, Image as ImageIcon, X, Check, AlertTriangle,
  UtensilsCrossed, BookOpen, Sparkles, ChefHat, Wine,
  UploadCloud, DollarSign, Grid3x3, List
} from 'lucide-react';

/* ── Allergen list ──────────────────────────────────────────── */
const ALLERGEN_LIST = [
  { id: 'gluten',     label: 'Gluten',     emoji: '🌾' },
  { id: 'lacteos',    label: 'Lácteos',    emoji: '🥛' },
  { id: 'huevo',      label: 'Huevo',      emoji: '🥚' },
  { id: 'pescado',    label: 'Pescado',    emoji: '🐟' },
  { id: 'mariscos',   label: 'Mariscos',   emoji: '🦐' },
  { id: 'nueces',     label: 'Nueces',     emoji: '🌰' },
  { id: 'cacahuates', label: 'Cacahuates', emoji: '🥜' },
  { id: 'soya',       label: 'Soya',       emoji: '🫘' },
  { id: 'apio',       label: 'Apio',       emoji: '🥬' },
  { id: 'mostaza',    label: 'Mostaza',    emoji: '🌿' },
  { id: 'sesamo',     label: 'Sésamo',     emoji: '🌱' },
  { id: 'sulfitos',   label: 'Sulfitos',   emoji: '🍷' },
  { id: 'altramuces', label: 'Altramuces', emoji: '🌼' },
  { id: 'moluscos',   label: 'Moluscos',   emoji: '🐚' },
];

const DEFAULT_CATEGORIES = [
  'General','Entradas','Sopas','Ensaladas','Pastas',
  'Platos Fuertes','Postres','Bebidas','Cócteles',
  'Vinos Tintos','Vinos Blancos','Espumosos',
];

const PRESETS = [
  { key:'ejecutivo', name:'Menú Ejecutivo',  Icon:ChefHat,        color:'#64748b', count:'6 platillos',  desc:'Entradas, sopas, fuertes, postres y bebidas' },
  { key:'vinos',     name:'Carta de Vinos',  Icon:Wine,           color:'#7c3aed', count:'5 vinos',      desc:'Tintos, blancos y espumosos' },
  { key:'completo',  name:'Carta Completa',  Icon:UtensilsCrossed,color:'#f97316', count:'11 platillos', desc:'Menú completo con todas las categorías' },
  { key:'mariscos',  name:'Carta de Mariscos', Icon:UtensilsCrossed, color:'#0284c7', count:'7 platillos', desc:'Especialidades del mar y pescados frescos' },
  { key:'italiana',  name:'Cocina Italiana',  Icon:UtensilsCrossed, color:'#16a34a', count:'8 platillos', desc:'Pastas, pizzas y clásicos italianos' },
  { key:'mexicana',  name:'Mexicana Tradicional', Icon:UtensilsCrossed, color:'#dc2626', count:'7 platillos', desc:'Sabores auténticos de México' },
  { key:'asiatica',  name:'Asiática Fusión',  Icon:UtensilsCrossed, color:'#db2777', count:'7 platillos', desc:'Sushi, ramen, wok y dumplings' },
  { key:'brunch',    name:'Brunch',           Icon:ChefHat,         color:'#f59e0b', count:'8 platillos', desc:'Desayunos y brunch para fin de semana' },
  { key:'vegana',    name:'Vegana',           Icon:UtensilsCrossed, color:'#22c55e', count:'7 platillos', desc:'Platillos 100% basados en plantas' },
  { key:'postres',   name:'Carta de Postres', Icon:UtensilsCrossed, color:'#a855f7', count:'8 postres',   desc:'Selección dulce de la casa' },
];

function toBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
function getEmbedUrl(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([^?&\s]+)/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
}

/* ── CSS helpers ────────────────────────────────────────────── */
const cls = (...parts) => parts.filter(Boolean).join(' ');

/* ── Pill toggle ────────────────────────────────────────────── */
function Toggle({ checked, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      style={{ width:44, height:24, borderRadius:12, background: checked ? '#f97316' : '#d1d5db', border:'none', cursor:'pointer', position:'relative', transition:'background 0.2s', flexShrink:0 }}
    >
      <span style={{ position:'absolute', top:2, left: checked ? 22 : 2, width:20, height:20, borderRadius:'50%', background:'#fff', transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }} />
    </button>
  );
}

/* ── Photo carousel ─────────────────────────────────────────── */
function PhotoRow({ photos, mainIdx, onSetMain, onRemove }) {
  if (!photos || !photos.length) return null;
  return (
    <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:8 }}>
      {photos.map((p, i) => (
        <div key={i} style={{ position:'relative', width:72, height:72, borderRadius:8, overflow:'hidden', border: i === mainIdx ? '2px solid #f97316' : '2px solid #e5e7eb' }}>
          <img src={p} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
          <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.4)', display:'flex', gap:4, alignItems:'center', justifyContent:'center' }}>
            <button onClick={() => onSetMain(i)} title="Foto principal" style={{ background: i===mainIdx ? '#f97316' : 'rgba(255,255,255,0.8)', border:'none', borderRadius:4, width:22, height:22, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <Star style={{ width:12, height:12, color: i===mainIdx ? '#fff' : '#374151' }} />
            </button>
            <button onClick={() => onRemove(i)} style={{ background:'rgba(239,68,68,0.9)', border:'none', borderRadius:4, width:22, height:22, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <X style={{ width:12, height:12, color:'#fff' }} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Dish card ──────────────────────────────────────────────── */
function DishCard({ dish, onEdit, onDelete, onToggleFeatured }) {
  const [hovered, setHovered] = useState(false);
  const photo = Array.isArray(dish.photos) && dish.photos.length > 0 ? dish.photos[dish.main_photo_idx || 0] : null;
  const embed = getEmbedUrl(dish.video_url);
  const allergens = Array.isArray(dish.allergens) ? dish.allergens : [];
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ border:'1px solid #e5e7eb', borderRadius:12, overflow:'hidden', background:'var(--card, #fff)', display:'flex', flexDirection:'column', transition:'box-shadow 0.2s', boxShadow: hovered ? '0 4px 16px rgba(0,0,0,0.1)' : 'none' }}
    >
      {embed ? (
        <div style={{ height:120, background:'#111', flexShrink:0 }}>
          <iframe src={embed} style={{ width:'100%', height:'100%', border:'none' }} allowFullScreen title={dish.name} />
        </div>
      ) : photo ? (
        <img src={photo} alt={dish.name} style={{ height:120, width:'100%', objectFit:'cover', flexShrink:0, display:'block' }} />
      ) : (
        <div style={{ height:120, background:'#f3f4f6', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
          <ImageIcon style={{ width:32, height:32, color:'#9ca3af' }} />
        </div>
      )}
      <div style={{ padding:'10px 12px', flex:1, display:'flex', flexDirection:'column', gap:4 }}>
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:4 }}>
          <p style={{ fontWeight:600, fontSize:13, margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1 }}>{dish.name}</p>
          <button onClick={() => onToggleFeatured(dish)} style={{ background:'none', border:'none', cursor:'pointer', padding:2, flexShrink:0 }}>
            <Star style={{ width:14, height:14, color: dish.is_featured ? '#f59e0b' : '#9ca3af', fill: dish.is_featured ? '#f59e0b' : 'none' }} />
          </button>
        </div>
        {dish.description && <p style={{ fontSize:11, color:'#6b7280', margin:0, overflow:'hidden', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical' }}>{dish.description}</p>}
        <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap', marginTop:'auto' }}>
          <span style={{ fontSize:10, padding:'2px 6px', background:'#f3f4f6', borderRadius:4, color:'#6b7280' }}>{dish.category || 'General'}</span>
          {dish.price != null && <span style={{ fontSize:12, fontWeight:700, color:'#16a34a' }}>${Number(dish.price).toFixed(2)}</span>}
          {!dish.is_active && <span style={{ fontSize:10, padding:'2px 6px', background:'#fef9c3', color:'#92400e', borderRadius:4 }}>Inactivo</span>}
        </div>
        {allergens.length > 0 && (
          <div style={{ display:'flex', gap:2, flexWrap:'wrap' }}>
            {allergens.slice(0,5).map(a => { const f = ALLERGEN_LIST.find(al => al.id === a); return <span key={a} style={{ fontSize:13 }} title={f?.label}>{f?.emoji || '⚠️'}</span>; })}
            {allergens.length > 5 && <span style={{ fontSize:10, color:'#9ca3af' }}>+{allergens.length-5}</span>}
          </div>
        )}
      </div>
      <div style={{ padding:'0 12px 10px', display:'flex', gap:6, opacity: hovered ? 1 : 0, transition:'opacity 0.2s' }}>
        <button onClick={() => onEdit(dish)} style={{ flex:1, padding:'5px 0', border:'1px solid #e5e7eb', borderRadius:6, background:'#fff', cursor:'pointer', fontSize:12, fontWeight:500, display:'flex', alignItems:'center', justifyContent:'center', gap:4 }}>
          <Edit2 style={{ width:12, height:12 }} /> Editar
        </button>
        <button onClick={() => onDelete(dish)} style={{ padding:'5px 8px', border:'1px solid #fecaca', borderRadius:6, background:'#fff', cursor:'pointer', color:'#ef4444' }}>
          <Trash2 style={{ width:12, height:12 }} />
        </button>
      </div>
    </div>
  );
}

/* ── Overlay ────────────────────────────────────────────────── */
function Modal({ open, onClose, title, children, wide }) {
  if (!open) return null;
  return (
    <div style={{ position:'fixed', inset:0, zIndex:1000, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', padding:16 }} onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:16, boxShadow:'0 25px 50px rgba(0,0,0,0.25)', width:'100%', maxWidth: wide ? 640 : 480, maxHeight:'90vh', display:'flex', flexDirection:'column', overflow:'hidden' }} onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'16px 20px', borderBottom:'1px solid var(--border, #e5e7eb)', flexShrink:0 }}>
          <h2 style={{ margin:0, fontSize:16, fontWeight:600 }}>{title}</h2>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'#9ca3af', padding:4, display:'flex', alignItems:'center', justifyContent:'center' }}>
            <X style={{ width:18, height:18 }} />
          </button>
        </div>
        <div style={{ overflowY:'auto', padding:20, flex:1 }}>{children}</div>
      </div>
    </div>
  );
}

/* ── Dish Form ──────────────────────────────────────────────── */
function DishForm({ dish, categories, onSave, onClose }) {
  const fileRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [customCat, setCustomCat] = useState('');
  const [form, setForm] = useState({
    name: dish?.name || '',
    description: dish?.description || '',
    price: dish?.price != null ? String(dish.price) : '',
    cost: dish?.cost != null ? String(dish.cost) : '',
    category: dish?.category || 'General',
    photos: Array.isArray(dish?.photos) ? dish.photos : [],
    main_photo_idx: dish?.main_photo_idx || 0,
    video_url: dish?.video_url || '',
    allergens: Array.isArray(dish?.allergens) ? dish.allergens : [],
    is_featured: dish?.is_featured || false,
    is_active: dish?.is_active !== false,
  });

  const setF = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const handlePhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const b64s = await Promise.all(files.map(toBase64));
      setForm(p => ({ ...p, photos: [...p.photos, ...b64s] }));
    } catch { toast.error('Error al procesar imagen'); }
    finally { setUploading(false); }
  };

  const removePhoto = (i) => setForm(p => ({ ...p, photos: p.photos.filter((_, idx) => idx !== i), main_photo_idx: Math.min(p.main_photo_idx, Math.max(0, p.photos.length - 2)) }));
  const toggleAllergen = (id) => setF('allergens', form.allergens.includes(id) ? form.allergens.filter(a => a !== id) : [...form.allergens, id]);

  const save = async () => {
    if (!form.name.trim()) { toast.error('El nombre es requerido'); return; }
    setSaving(true);
    try {
      await onSave({ ...form, price: form.price !== '' ? parseFloat(form.price) : null, cost: form.cost !== '' ? parseFloat(form.cost) : null, category: customCat.trim() || form.category });
      onClose();
    } catch { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const allCats = [...new Set([...DEFAULT_CATEGORIES, ...categories])];
  const embed = getEmbedUrl(form.video_url);
  const inp = { border:'1px solid var(--border, #e5e7eb)', borderRadius:8, padding:'8px 12px', fontSize:13, width:'100%', boxSizing:'border-box', background:'#fff', color:'inherit', outline:'none', fontFamily:'inherit' };
  const lbl = { fontSize:12, fontWeight:600, color:'#6b7280', display:'block', marginBottom:4 };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      <div><label style={lbl}>Nombre *</label><input style={inp} value={form.name} onChange={e => setF('name', e.target.value)} placeholder="Filete a la parrilla" /></div>
      <div><label style={lbl}>Descripción</label><textarea style={{ ...inp, resize:'vertical', minHeight:60 }} value={form.description} onChange={e => setF('description', e.target.value)} placeholder="Descripción del platillo..." /></div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <div><label style={lbl}>Precio</label><div style={{ position:'relative' }}><DollarSign style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', width:14, height:14, color:'#9ca3af' }} /><input style={{ ...inp, paddingLeft:28 }} type="number" min="0" step="0.01" value={form.price} onChange={e => setF('price', e.target.value)} placeholder="0.00" /></div></div>
        <div><label style={lbl}>Costo (interno)</label><div style={{ position:'relative' }}><DollarSign style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', width:14, height:14, color:'#9ca3af' }} /><input style={{ ...inp, paddingLeft:28 }} type="number" min="0" step="0.01" value={form.cost} onChange={e => setF('cost', e.target.value)} placeholder="0.00" /></div></div>
      </div>

      <div>
        <label style={lbl}>Categoría</label>
        <div style={{ display:'flex', gap:8 }}>
          <select style={{ ...inp, flex:1 }} value={form.category} onChange={e => { setF('category', e.target.value); setCustomCat(''); }}>
            {allCats.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <input style={{ ...inp, flex:1 }} value={customCat} onChange={e => setCustomCat(e.target.value)} placeholder="Nueva categoría..." />
        </div>
        {customCat.trim() && <p style={{ fontSize:11, color:'#9ca3af', marginTop:4 }}>Se usará: <strong>{customCat.trim()}</strong></p>}
      </div>

      <div>
        <label style={lbl}>Fotos ({form.photos.length})</label>
        <PhotoRow photos={form.photos} mainIdx={form.main_photo_idx} onSetMain={i => setF('main_photo_idx', i)} onRemove={removePhoto} />
        <button onClick={() => fileRef.current?.click()} disabled={uploading} style={{ marginTop:8, border:'2px dashed #e5e7eb', borderRadius:8, padding:'10px 16px', cursor:'pointer', background:'transparent', fontSize:12, display:'flex', alignItems:'center', gap:6, color:'#9ca3af' }}>
          {uploading ? <Loader2 className="animate-spin" style={{ width:14, height:14}} /> : <UploadCloud style={{ width:14, height:14 }} />}
          {uploading ? 'Procesando...' : 'Subir fotos (múltiple)'}
        </button>
        <input ref={fileRef} type="file" accept="image/*" multiple style={{ display:'none' }} onChange={handlePhotos} />
      </div>

      <div>
        <label style={lbl}>Video YouTube (opcional)</label>
        <input style={inp} value={form.video_url} onChange={e => setF('video_url', e.target.value)} placeholder="https://youtube.com/watch?v=..." />
        {embed && <div style={{ marginTop:8, borderRadius:8, overflow:'hidden', aspectRatio:'16/9' }}><iframe src={embed} style={{ width:'100%', height:'100%', border:'none' }} allowFullScreen title="preview" /></div>}
      </div>

      <div>
        <label style={{ ...lbl, display:'flex', alignItems:'center', gap:6 }}><AlertTriangle style={{ width:14, height:14, color:'#f59e0b' }} /> Alérgenos</label>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:6 }}>
          {ALLERGEN_LIST.map(al => {
            const sel = form.allergens.includes(al.id);
            return (
              <button key={al.id} onClick={() => toggleAllergen(al.id)} style={{ padding:'6px 4px', borderRadius:8, border:`1px solid ${sel ? '#fbbf24' : '#e5e7eb'}`, background: sel ? '#fffbeb' : 'transparent', cursor:'pointer', fontSize:11, fontWeight:500, display:'flex', alignItems:'center', gap:4, transition:'all 0.1s' }}>
                <span>{al.emoji}</span><span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{al.label}</span>
                {sel && <Check style={{ width:10, height:10, color:'#d97706', marginLeft:'auto', flexShrink:0 }} />}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ display:'flex', gap:24, alignItems:'center', flexWrap:'wrap' }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <Toggle checked={form.is_featured} onChange={v => setF('is_featured', v)} />
          <span style={{ fontSize:13, display:'flex', alignItems:'center', gap:4 }}><Star style={{ width:12, height:12, color:'#f59e0b', fill:'#f59e0b' }} /> Destacado</span>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <Toggle checked={form.is_active} onChange={v => setF('is_active', v)} />
          <span style={{ fontSize:13 }}>Activo</span>
        </div>
      </div>

      <div style={{ display:'flex', justifyContent:'flex-end', gap:8, paddingTop:12, borderTop:'1px solid var(--border, #e5e7eb)' }}>
        <button onClick={onClose} disabled={saving} style={{ padding:'8px 16px', border:'1px solid #e5e7eb', borderRadius:8, background:'transparent', cursor:'pointer', fontSize:13 }}>Cancelar</button>
        <button onClick={save} disabled={saving} style={{ padding:'8px 20px', border:'none', borderRadius:8, background:'#f97316', color:'#fff', cursor:'pointer', fontSize:13, fontWeight:600, display:'flex', alignItems:'center', gap:6 }}>
          {saving ? <Loader2 className="animate-spin" style={{ width:14, height:14}} /> : <Check style={{ width:14, height:14 }} />}
          {dish ? 'Guardar cambios' : 'Crear platillo'}
        </button>
      </div>
    </div>
  );
}

/* ── Menu Form ──────────────────────────────────────────────── */
function MenuForm({ menu, onSave, onClose }) {
  const [form, setForm] = useState({ name: menu?.name || '', description: menu?.description || '', is_active: menu?.is_active !== false });
  const [saving, setSaving] = useState(false);
  const inp = { border:'1px solid var(--border, #e5e7eb)', borderRadius:8, padding:'8px 12px', fontSize:13, width:'100%', boxSizing:'border-box', background:'#fff', color:'inherit', outline:'none', fontFamily:'inherit' };
  const lbl = { fontSize:12, fontWeight:600, color:'#6b7280', display:'block', marginBottom:4 };

  const save = async () => {
    if (!form.name.trim()) { toast.error('El nombre es requerido'); return; }
    setSaving(true);
    try { await onSave(form); onClose(); }
    catch { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      <div><label style={lbl}>Nombre *</label><input style={inp} value={form.name} onChange={e => setForm(p=>({...p,name:e.target.value}))} placeholder="Carta del día" /></div>
      <div><label style={lbl}>Descripción</label><textarea style={{ ...inp, resize:'vertical', minHeight:60 }} value={form.description} onChange={e => setForm(p=>({...p,description:e.target.value}))} placeholder="Breve descripción del menú" /></div>
      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
        <Toggle checked={form.is_active} onChange={v => setForm(p=>({...p,is_active:v}))} />
        <span style={{ fontSize:13 }}>Menú activo (visible en widget)</span>
      </div>
      <div style={{ display:'flex', justifyContent:'flex-end', gap:8, paddingTop:12, borderTop:'1px solid var(--border, #e5e7eb)' }}>
        <button onClick={onClose} disabled={saving} style={{ padding:'8px 16px', border:'1px solid #e5e7eb', borderRadius:8, background:'transparent', cursor:'pointer', fontSize:13 }}>Cancelar</button>
        <button onClick={save} disabled={saving} style={{ padding:'8px 20px', border:'none', borderRadius:8, background:'#f97316', color:'#fff', cursor:'pointer', fontSize:13, fontWeight:600, display:'flex', alignItems:'center', gap:6 }}>
          {saving ? <Loader2 className="animate-spin" style={{ width:14, height:14 }} /> : <Check style={{ width:14, height:14 }} />}
          {menu ? 'Guardar cambios' : 'Crear menú'}
        </button>
      </div>
    </div>
  );
}

/* ── Main ───────────────────────────────────────────────────── */
export default function MenusCartas() {
  const [menus, setMenus] = useState([]);
  const [dishes, setDishes] = useState([]);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [loadingMenus, setLoadingMenus] = useState(false);
  const [loadingDishes, setLoadingDishes] = useState(false);
  const [catFilter, setCatFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [search, setSearch] = useState('');

  const [menuModal, setMenuModal] = useState({ open:false, menu:null });
  const [dishModal, setDishModal] = useState({ open:false, dish:null });
  const [presetsOpen, setPresetsOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [applyingPreset, setApplyingPreset] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const normalizeDish = (d) => ({
    ...d,
    photos: Array.isArray(d?.photos) ? d.photos : [],
    allergens: Array.isArray(d?.allergens) ? d.allergens : [],
    name: d?.name || '',
    description: d?.description || '',
    category: d?.category || 'General',
  });

  const loadMenus = useCallback(async () => {
    setLoadingMenus(true);
    try {
      const r = await api.get('/menus');
      const data = Array.isArray(r.data) ? r.data : (Array.isArray(r.data?.items) ? r.data.items : []);
      setMenus(data);
    } catch { toast.error('Error al cargar menús'); }
    finally { setLoadingMenus(false); }
  }, []);

  const loadDishes = useCallback(async (id) => {
    if (!id) { setDishes([]); return; }
    setLoadingDishes(true);
    try {
      const r = await api.get(`/menus/${id}/dishes`);
      const data = Array.isArray(r.data) ? r.data.map(normalizeDish) : [];
      setDishes(data);
    } catch { toast.error('Error al cargar platillos'); }
    finally { setLoadingDishes(false); }
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadMenus(); }, [loadMenus]);

  useEffect(() => {
    const id = selectedMenu ? selectedMenu.id : null;
    loadDishes(id);
    setCatFilter('all');
    setSearch('');
  }, [selectedMenu, loadDishes]);

  const saveMenu = useCallback(async (form) => {
    if (menuModal.menu) { await api.put(`/menus/${menuModal.menu.id}`, form); toast.success('Menú actualizado'); }
    else { const r = await api.post('/menus', form); setSelectedMenu(r.data); toast.success('Menú creado'); }
    await loadMenus();
  }, [menuModal.menu, loadMenus]);

  const saveDish = useCallback(async (form) => {
    if (!selectedMenu) return;
    if (dishModal.dish) { await api.put(`/menus/${selectedMenu.id}/dishes/${dishModal.dish.id}`, form); toast.success('Platillo actualizado'); }
    else { await api.post(`/menus/${selectedMenu.id}/dishes`, form); toast.success('Platillo creado'); }
    await loadDishes(selectedMenu.id);
  }, [selectedMenu, dishModal.dish, loadDishes]);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      if (deleteTarget.type === 'menu') {
        await api.delete(`/menus/${deleteTarget.item.id}`);
        if (selectedMenu && selectedMenu.id === deleteTarget.item.id) setSelectedMenu(null);
        toast.success('Menú eliminado');
        await loadMenus();
      } else {
        await api.delete(`/menus/${selectedMenu.id}/dishes/${deleteTarget.item.id}`);
        toast.success('Platillo eliminado');
        await loadDishes(selectedMenu.id);
      }
    } catch { toast.error('Error al eliminar'); }
    finally { setDeleting(false); setDeleteTarget(null); }
  }, [deleteTarget, selectedMenu, loadMenus, loadDishes]);

  const toggleMenuActive = useCallback(async (menu) => {
    try { await api.put(`/menus/${menu.id}`, { is_active: !menu.is_active }); await loadMenus(); }
    catch { toast.error('Error al actualizar'); }
  }, [loadMenus]);

  const toggleFeatured = useCallback(async (dish) => {
    if (!selectedMenu) return;
    try { await api.put(`/menus/${selectedMenu.id}/dishes/${dish.id}`, { is_featured: !dish.is_featured }); await loadDishes(selectedMenu.id); }
    catch { toast.error('Error al actualizar'); }
  }, [selectedMenu, loadDishes]);

  const applyPreset = useCallback(async (key) => {
    setApplyingPreset(key);
    try {
      const r = await api.post(`/menus/presets/${key}`);
      await loadMenus();
      toast.success(`"${r.data.menu_name}" creado con platillos`);
      setPresetsOpen(false);
    } catch { toast.error('Error al aplicar plantilla'); }
    finally { setApplyingPreset(null); }
  }, [loadMenus]);

  /* derived — always guard against non-array states */
  const safeMenus = Array.isArray(menus) ? menus : [];
  const safeDishes = Array.isArray(dishes) ? dishes : [];
  const categories = [...new Set(safeDishes.map(d => d?.category).filter(Boolean))];
  const filtered = safeDishes.filter(d => {
    if (!d || typeof d !== 'object') return false;
    if (catFilter !== 'all' && d.category !== catFilter) return false;
    if (search && !(d.name || '').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  /* style helpers */
  const s = {
    leftPanel: { width:256, flexShrink:0, borderRight:'1px solid var(--border, #e5e7eb)', display:'flex', flexDirection:'column', background:'var(--card, #fafafa)' },
    iconBtn: (active) => ({ padding:6, borderRadius:6, border:'none', cursor:'pointer', background: active ? '#f97316' : 'transparent', color: active ? '#fff' : '#6b7280', display:'flex', alignItems:'center', justifyContent:'center' }),
    menuItem: (active) => ({ padding:'8px 10px', borderRadius:8, cursor:'pointer', border:`1px solid ${active ? '#fdba74' : 'transparent'}`, background: active ? '#fff7ed' : 'transparent', transition:'all 0.15s', position:'relative' }),
    catPill: (active) => ({ padding:'4px 12px', borderRadius:20, border:`1px solid ${active ? '#f97316' : '#e5e7eb'}`, background: active ? '#f97316' : 'transparent', color: active ? '#fff' : 'inherit', fontSize:12, fontWeight:500, cursor:'pointer', whiteSpace:'nowrap', flexShrink:0 }),
    viewBtn: (active) => ({ padding:5, border:'none', background: active ? 'var(--accent, #f3f4f6)' : 'transparent', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:'#6b7280' }),
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', overflow:'hidden' }}>
      <TopBar title="Menús & Cartas" helpSection="menus-cartas" />

      <div style={{ display:'flex', flex:1, overflow:'hidden' }}>

        {/* ── Left panel ── */}
        <div style={s.leftPanel}>
          <div style={{ padding:'12px', borderBottom:'1px solid var(--border, #e5e7eb)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <span style={{ fontWeight:700, fontSize:14 }}>Menús</span>
            <div style={{ display:'flex', gap:4 }}>
              <button style={{ ...s.iconBtn(false), color:'#8b5cf6' }} title="Plantillas" onClick={() => setPresetsOpen(true)}><Sparkles style={{ width:16, height:16 }} /></button>
              <button style={s.iconBtn(true)} title="Nuevo menú" onClick={() => setMenuModal({ open:true, menu:null })}><Plus style={{ width:16, height:16 }} /></button>
            </div>
          </div>

          <div style={{ flex:1, overflowY:'auto', padding:8 }}>
            {loadingMenus && <div style={{ display:'flex', justifyContent:'center', padding:24 }}><Loader2 className="animate-spin" style={{ width:20, height:20, color:'#f97316'}} /></div>}
            {!loadingMenus && safeMenus.length === 0 && (
              <div style={{ textAlign:'center', padding:'24px 12px' }}>
                <BookOpen style={{ width:32, height:32, color:'#d1d5db', margin:'0 auto 8px' }} />
                <p style={{ fontSize:12, color:'#9ca3af', margin:0 }}>Sin menús. Crea uno o usa una plantilla.</p>
              </div>
            )}
            {safeMenus.map(menu => {
              const active = selectedMenu && selectedMenu.id === menu.id;
              return (
                <div key={menu.id} style={s.menuItem(active)} onClick={() => setSelectedMenu(menu)}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                    <p style={{ fontWeight:600, fontSize:13, margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1 }}>{menu.name}</p>
                    <div style={{ display:'flex', gap:2, flexShrink:0 }}>
                      <button onClick={e => { e.stopPropagation(); setMenuModal({ open:true, menu }); }} style={{ padding:3, border:'none', background:'transparent', cursor:'pointer', color:'#9ca3af', display:'flex' }}><Edit2 style={{ width:12, height:12 }} /></button>
                      <button onClick={e => { e.stopPropagation(); setDeleteTarget({ type:'menu', item:menu }); }} style={{ padding:3, border:'none', background:'transparent', cursor:'pointer', color:'#ef4444', display:'flex' }}><Trash2 style={{ width:12, height:12 }} /></button>
                    </div>
                  </div>
                  <button onClick={e => { e.stopPropagation(); toggleMenuActive(menu); }} style={{ marginTop:4, padding:'2px 8px', borderRadius:20, border:'none', cursor:'pointer', fontSize:11, fontWeight:600, background: menu.is_active ? '#dcfce7' : '#f3f4f6', color: menu.is_active ? '#166534' : '#6b7280' }}>
                    {menu.is_active ? '● Activo' : '○ Inactivo'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Right panel ── */}
        <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden' }}>
          {!selectedMenu ? (
            <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center' }}>
              <div style={{ textAlign:'center', maxWidth:280 }}>
                <UtensilsCrossed style={{ width:56, height:56, color:'#d1d5db', margin:'0 auto 16px' }} />
                <h3 style={{ fontWeight:700, fontSize:18, margin:'0 0 8px' }}>Selecciona un menú</h3>
                <p style={{ fontSize:14, color:'#9ca3af', margin:'0 0 16px' }}>Elige un menú del panel izquierdo para gestionar sus platillos</p>
                {safeMenus.length === 0 && (
                  <div style={{ display:'flex', gap:8, justifyContent:'center', flexWrap:'wrap' }}>
                    <button onClick={() => setPresetsOpen(true)} style={{ padding:'7px 14px', border:'1px solid #e5e7eb', borderRadius:8, background:'transparent', cursor:'pointer', fontSize:13, display:'flex', alignItems:'center', gap:6 }}>
                      <Sparkles style={{ width:14, height:14, color:'#8b5cf6' }} /> Plantilla
                    </button>
                    <button onClick={() => setMenuModal({ open:true, menu:null })} style={{ padding:'7px 14px', border:'none', borderRadius:8, background:'#f97316', color:'#fff', cursor:'pointer', fontSize:13, fontWeight:600, display:'flex', alignItems:'center', gap:6 }}>
                      <Plus style={{ width:14, height:14 }} /> Nuevo menú
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              {/* Header */}
              <div style={{ padding:'10px 16px', borderBottom:'1px solid var(--border, #e5e7eb)', display:'flex', alignItems:'center', gap:10, flexShrink:0, flexWrap:'wrap' }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <p style={{ fontWeight:700, fontSize:14, margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{selectedMenu.name}</p>
                  <p style={{ fontSize:11, color:'#9ca3af', margin:0 }}>{dishes.length} platillos</p>
                </div>
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar..." style={{ padding:'6px 10px', border:'1px solid var(--border, #e5e7eb)', borderRadius:8, fontSize:13, width:140, outline:'none', background:'#fff', color:'inherit' }} />
                <div style={{ display:'flex', border:'1px solid var(--border, #e5e7eb)', borderRadius:6, overflow:'hidden' }}>
                  <button style={s.viewBtn(viewMode==='grid')} onClick={() => setViewMode('grid')}><Grid3x3 style={{ width:14, height:14 }} /></button>
                  <button style={s.viewBtn(viewMode==='list')} onClick={() => setViewMode('list')}><List style={{ width:14, height:14 }} /></button>
                </div>
                <button onClick={() => setDishModal({ open:true, dish:null })} style={{ padding:'7px 12px', border:'none', borderRadius:8, background:'#f97316', color:'#fff', cursor:'pointer', fontSize:13, fontWeight:600, display:'flex', alignItems:'center', gap:6, flexShrink:0 }}>
                  <Plus style={{ width:14, height:14 }} /> Platillo
                </button>
              </div>

              {/* Category pills */}
              {categories.length > 0 && (
                <div style={{ padding:'8px 16px', display:'flex', gap:6, overflowX:'auto', flexShrink:0 }}>
                  <button style={s.catPill(catFilter==='all')} onClick={() => setCatFilter('all')}>Todos ({dishes.length})</button>
                  {categories.map(c => <button key={c} style={s.catPill(catFilter===c)} onClick={() => setCatFilter(c)}>{c} ({dishes.filter(d=>d.category===c).length})</button>)}
                </div>
              )}

              {/* Dish grid / list */}
              <div style={{ flex:1, overflowY:'auto', padding:16 }}>
                {loadingDishes && <div style={{ display:'flex', justifyContent:'center', padding:48 }}><Loader2 className="animate-spin" style={{ width:24, height:24, color:'#f97316'}} /></div>}

                {!loadingDishes && filtered.length === 0 && (
                  <div style={{ textAlign:'center', padding:'48px 0' }}>
                    <ChefHat style={{ width:40, height:40, color:'#d1d5db', margin:'0 auto 12px' }} />
                    <p style={{ fontSize:14, color:'#9ca3af', marginBottom:12 }}>{search || catFilter !== 'all' ? 'Sin resultados' : 'Sin platillos aún'}</p>
                    {catFilter === 'all' && !search && (
                      <button onClick={() => setDishModal({ open:true, dish:null })} style={{ padding:'7px 16px', border:'none', borderRadius:8, background:'#f97316', color:'#fff', cursor:'pointer', fontSize:13, fontWeight:600, display:'inline-flex', alignItems:'center', gap:6 }}>
                        <Plus style={{ width:14, height:14 }} /> Agregar primer platillo
                      </button>
                    )}
                  </div>
                )}

                {!loadingDishes && filtered.length > 0 && viewMode === 'grid' && (
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:12 }}>
                    {filtered.map(dish => (
                      <DishCard key={dish.id} dish={dish}
                        onEdit={d => setDishModal({ open:true, dish:d })}
                        onDelete={d => setDeleteTarget({ type:'dish', item:d })}
                        onToggleFeatured={toggleFeatured} />
                    ))}
                  </div>
                )}

                {!loadingDishes && filtered.length > 0 && viewMode === 'list' && (
                  <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                    {filtered.map(dish => {
                      const photo = Array.isArray(dish.photos) && dish.photos.length > 0 ? dish.photos[dish.main_photo_idx||0] : null;
                      return (
                        <div key={dish.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 12px', border:'1px solid var(--border, #e5e7eb)', borderRadius:10, background:'var(--card, #fff)' }}>
                          {photo ? <img src={photo} alt="" style={{ width:48, height:48, borderRadius:8, objectFit:'cover', flexShrink:0 }} /> : <div style={{ width:48, height:48, borderRadius:8, background:'#f3f4f6', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center' }}><ImageIcon style={{ width:18, height:18, color:'#9ca3af' }} /></div>}
                          <div style={{ flex:1, minWidth:0 }}>
                            <p style={{ fontWeight:600, fontSize:13, margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{dish.name}{dish.is_featured && <Star style={{ width:12, height:12, color:'#f59e0b', fill:'#f59e0b', marginLeft:4, display:'inline' }} />}</p>
                            {dish.description && <p style={{ fontSize:11, color:'#9ca3af', margin:'2px 0 0', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{dish.description}</p>}
                            <div style={{ display:'flex', gap:6, marginTop:2, flexWrap:'wrap', alignItems:'center' }}>
                              <span style={{ fontSize:10, padding:'1px 6px', background:'#f3f4f6', borderRadius:4, color:'#6b7280' }}>{dish.category}</span>
                              {dish.price != null && <span style={{ fontSize:11, fontWeight:700, color:'#16a34a' }}>${Number(dish.price).toFixed(2)}</span>}
                            </div>
                          </div>
                          <div style={{ display:'flex', gap:4 }}>
                            <button onClick={() => setDishModal({ open:true, dish })} style={{ padding:'5px 10px', border:'1px solid #e5e7eb', borderRadius:6, background:'transparent', cursor:'pointer', fontSize:12, display:'flex', alignItems:'center', gap:4 }}><Edit2 style={{ width:11, height:11 }} /></button>
                            <button onClick={() => setDeleteTarget({ type:'dish', item:dish })} style={{ padding:'5px 8px', border:'1px solid #fecaca', borderRadius:6, background:'transparent', cursor:'pointer', color:'#ef4444' }}><Trash2 style={{ width:11, height:11 }} /></button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Modals ── */}
      <Modal open={menuModal.open} onClose={() => setMenuModal({ open:false, menu:null })} title={menuModal.menu ? 'Editar Menú' : 'Nuevo Menú'}>
        {menuModal.open && <MenuForm menu={menuModal.menu} onSave={saveMenu} onClose={() => setMenuModal({ open:false, menu:null })} />}
      </Modal>

      <Modal open={dishModal.open} onClose={() => setDishModal({ open:false, dish:null })} title={dishModal.dish ? 'Editar platillo' : 'Nuevo platillo'} wide>
        {dishModal.open && <DishForm dish={dishModal.dish} categories={categories} onSave={saveDish} onClose={() => setDishModal({ open:false, dish:null })} />}
      </Modal>

      <Modal open={presetsOpen} onClose={() => setPresetsOpen(false)} title="Crear desde plantilla">
        {presetsOpen && (
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            <p style={{ fontSize:13, color:'#9ca3af', margin:0 }}>Crea un menú completo con platillos preconfigurados en un clic.</p>
            {PRESETS.map(({ key, name, Icon, color, count, desc }) => (
              <div key={key} style={{ display:'flex', alignItems:'center', gap:14, padding:'12px 16px', border:'1px solid var(--border, #e5e7eb)', borderRadius:10 }}>
                <div style={{ width:44, height:44, borderRadius:10, background:color, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <Icon style={{ width:22, height:22, color:'#fff' }} />
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <p style={{ fontWeight:700, fontSize:13, margin:0 }}>{name}</p>
                  <p style={{ fontSize:11, color:'#9ca3af', margin:'2px 0 4px' }}>{desc}</p>
                  <span style={{ fontSize:10, padding:'2px 8px', background:'#f3f4f6', borderRadius:4, color:'#6b7280' }}>{count}</span>
                </div>
                <button disabled={applyingPreset === key} onClick={() => applyPreset(key)} style={{ padding:'7px 14px', border:'none', borderRadius:8, background:'#f97316', color:'#fff', cursor:'pointer', fontSize:13, fontWeight:600, display:'flex', alignItems:'center', gap:6, flexShrink:0 }}>
                  {applyingPreset === key ? <Loader2 className="animate-spin" style={{ width:14, height:14}} /> : 'Usar'}
                </button>
              </div>
            ))}
          </div>
        )}
      </Modal>

      <Modal open={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="¿Confirmar eliminación?">
        {deleteTarget && (
          <div>
            <p style={{ fontSize:14, color:'#9ca3af', marginBottom:20 }}>
              {deleteTarget.type === 'menu'
                ? `Se eliminará el menú "${deleteTarget.item?.name}" y todos sus platillos.`
                : `Se eliminará el platillo "${deleteTarget.item?.name}".`
              } Esta acción no se puede deshacer.
            </p>
            <div style={{ display:'flex', justifyContent:'flex-end', gap:8 }}>
              <button onClick={() => setDeleteTarget(null)} disabled={deleting} style={{ padding:'8px 16px', border:'1px solid #e5e7eb', borderRadius:8, background:'transparent', cursor:'pointer', fontSize:13 }}>Cancelar</button>
              <button onClick={handleDelete} disabled={deleting} style={{ padding:'8px 16px', border:'none', borderRadius:8, background:'#ef4444', color:'#fff', cursor:'pointer', fontSize:13, fontWeight:600, display:'flex', alignItems:'center', gap:6 }}>
                {deleting ? <Loader2 className="animate-spin" style={{ width:14, height:14 }} /> : <Trash2 style={{ width:14, height:14 }} />} Eliminar
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
